/**
 * The one surface for recording a call, wherever it is shown.
 *
 * The old console spread this across three columns and asked the caller to scan for
 * the field they wanted while somebody was talking to them. This is a single column
 * with an order of importance, because during a call there is only ever one next
 * thing to do:
 *
 *   1. the notepad, given most of the space — what is being said, as it is said
 *   2. what happened — the module's own outcomes, one row of chips
 *   3. what next — only after an outcome, because before that it means nothing
 *
 * Everything reference-shaped (the note history, the customer's numbers) is behind a
 * disclosure, closed by default: useful before dialling, noise during the call.
 *
 * The dock and the full page render this same component, so they cannot drift.
 */
import React, { useEffect, useState } from 'react';
import {
  FaPhoneSlash, FaCheck, FaRegClock, FaChevronDown, FaChevronUp, FaMobileAlt, FaFilePdf,
  FaLayerGroup,
} from 'react-icons/fa';
import {
  useFeedbackForm, useRecordNotes, useRecord, useAddContact, useUpdateContact, apiMessage,
} from '../hooks/useMobileOrgApi';
import NoteHistory from '../components/NoteHistory';
import QuotationDialog from '../components/QuotationDialog';

const mmss = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, '0')}`;

/** One labelled box in the contact panel — small, because it is a secondary task. */
function ContactField({ label, k, v, set, ph }) {
  return (
    <label className="lc-contactfield">
      <span>{label}</span>
      <input
        className="lc-input"
        value={v[k] || ''}
        placeholder={ph}
        onChange={(e) => set((f) => ({ ...f, [k]: e.target.value }))}
      />
    </label>
  );
}

const NEXT_ACTIONS = [
  'Follow-up call', 'Send WhatsApp quote', 'Send proforma invoice',
  'Send catalogue', 'Schedule meeting', 'Send e-mail', 'No further action',
];

const WHEN = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
  { label: 'In 2 weeks', days: 14 },
];

const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(10, 0, 0, 0);
  return d;
};

const toLocalInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const sameDay = (a, b) => a && b && new Date(a).toDateString() === new Date(b).toDateString();

export default function LiveCallPanel({ live, variant = 'full' }) {
  const { item, feedback, seconds, savedAt, error, isLive, saving } = live;

  const form = useFeedbackForm(item?.module, !!item);
  const [showContext, setShowContext] = useState(false);

  /**
   * Details learned on the call — the same panel the phone shows.
   *
   * An account with nobody on file is rung on its office line, and whoever answers is
   * often the first real contact anybody has had. Capturing them here, while the name is
   * still in the caller's head, is the difference between a contact list that grows and
   * one that stays empty. Where a person *was* rung, the same panel corrects what is on
   * file — which is how an e-mail obtained on the call gets recorded.
   */
  const known = live.call?.contact?.id ? live.call.contact : null;
  const canKeepContacts = item?.module === 'cold_calling.current_customer'
    || item?.module === 'cold_calling.target_customer';
  const [showContact, setShowContact] = useState(false);
  /*
   * Whether these details belong to whoever answered, or to somebody else. Ringing a
   * contact and being handed the right person's name is ordinary — a receptionist passes
   * you to purchasing — and that name is a new contact, not a correction to the one who
   * picked up.
   */
  const [contactMode, setContactMode] = useState('update');
  const addingNew = !known || contactMode === 'add';
  /**
   * Fetched only when opened. On a Follow Up call the quotation is usually the first
   * thing discussed, but most calls never need it rendered — so it stays one click away
   * rather than loading a PDF into every write-up.
   */
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [contactForm, setContactForm] = useState({});
  const addContact = useAddContact();
  const updateContact = useUpdateContact();

  /**
   * Everything on this panel belongs to one record, so a new record starts clean — the
   * same rule the phone follows when its write-up sheet closes.
   *
   * A run does not unmount between calls: saving one record swaps in the next while this
   * component stays mounted. Without this the caller would land on the next company with
   * the last one's quotation still open over the screen, their context panel expanded and
   * a half-typed contact name waiting to be saved against the wrong company.
   */
  useEffect(() => {
    setQuoteOpen(false);
    setShowContact(false);
    setContactMode('update');
    setShowContext(false);
    setContactForm({});
  }, [item?.module, item?.recordId]);

  const notes = useRecordNotes(item?.module, item?.recordId, !!item && showContext && variant === 'dock');
  const fetched = useRecord(item?.module, item?.recordId, !!item);
  const record = fetched.data;
  /** The queue row already carries it; the fetched record is the fallback for an
   *  adopted call, which only has the snapshot taken when it was dialled. */
  const hasQuotation = Boolean(item?.meta?.hasQuotation || record?.meta?.hasQuotation);

  if (!item) return null;

  const def = (form.data?.form?.groups || [])
    .flatMap((g) => g.outcomes)
    .find((o) => o.key === feedback.outcome) || null;

  const pick = (o) => {
    if (o.terminal) {
      live.update({ outcome: o.key, nextFollowUpAt: null, nextAction: 'No further action', fields: {} });
    } else if (o.requiresFollowUp && !feedback.nextFollowUpAt) {
      live.update({ outcome: o.key, nextFollowUpAt: addDays(3).toISOString(), fields: {} });
    } else {
      live.update({ outcome: o.key, fields: {} });
    }
  };

  const remarkRequired = form.data?.form?.requiresRemark || def?.requiresRemark;

  return (
    <div className={`lc-panel lc-${variant}`}>
      <header className={`lc-head${isLive ? ' live' : ''}`}>
        <span className="lc-dot" aria-hidden="true" />
        <div className="lc-who">
          <div className="lc-state">
            {isLive ? 'On the call' : 'Write it up'}
            {live.call?.snapshot?.phone ? '' : ''}
          </div>
          <h3 className="lc-company">{item.company || 'Unnamed record'}</h3>
          <p className="lc-contact">
            {item.contactPerson
              ? <>{item.contactPerson}{item.phone ? ` · ${item.phone}` : ''}</>
              : <>{item.phone || 'No number on file'}{item.phone && <span className="lc-linetag">office line</span>}</>}
          </p>
        </div>
        <div className="lc-headright">
          {hasQuotation && (
            <button
              type="button"
              className="lc-quotebtn"
              onClick={() => setQuoteOpen(true)}
              title="Open the quotation this call is about"
            >
              <FaFilePdf size={11} /> Quotation
            </button>
          )}
          <div className="lc-timer"><FaRegClock size={11} /> {mmss(seconds)}</div>
        </div>
      </header>

      <QuotationDialog
        open={quoteOpen}
        item={record || item}
        title={`${item.company || 'Record'}${item.rfqNo ? ` · ${item.rfqNo}` : ''}`}
        onClose={() => setQuoteOpen(false)}
      />

      {/*
        One card stands for every quotation this company had due, and the write-up is
        filed against all of them. Said while the call can still cover them, not after.
      */}
      {item.dueRfqCount > 1 && (
        <div className="lc-covers">
          <FaLayerGroup />
          <span>
            This call covers <strong>{item.dueRfqCount} quotations</strong> —{' '}
            {(item.dueRfqNumbers || []).join(', ')}. What you write is filed against each.
          </span>
        </div>
      )}

      {!!error && (
        <div className="lc-error" role="alert">
          {apiMessage(error)}
          <button type="button" className="scc-linkbtn" onClick={live.clearError}>Dismiss</button>
        </div>
      )}

      {/* 1 — the notepad. The reason this screen exists. */}
      <label className="lc-field">
        <span className="lc-label">
          {isLive ? 'What are they telling you?' : (form.data?.form?.remarkLabel || 'What was said')}
          {remarkRequired ? ' *' : ''}
        </span>
        <textarea
          className="lc-notepad"
          value={feedback.notes || ''}
          onChange={(e) => live.update({ notes: e.target.value })}
          placeholder="Type as they talk. What did they say? What did you promise?"
          rows={variant === 'dock' ? 5 : 8}
          autoFocus={variant === 'full'}
        />
        <span className="lc-saved">
          {savedAt
            ? `Saved ${savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`
            : feedback.notes ? 'Saving…' : 'Saved automatically as you type'}
        </span>
      </label>

      {/* 2 — what happened. */}
      <div className="lc-field">
        <span className="lc-label">How did it go?{!feedback.outcome ? ' *' : ''}</span>
        {form.isLoading ? (
          <p className="lc-muted">Loading this module’s outcomes…</p>
        ) : (
          (form.data?.form?.groups || []).map((group) => (
            <div key={group.title} className="lc-group">
              <span className="lc-grouplabel">{group.title}</span>
              <div className="lc-chips">
                {group.outcomes.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => pick(o)}
                    className={`lc-outcome tone-${o.tone || 'neutral'}${feedback.outcome === o.key ? ' on' : ''}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Extra values this outcome collects — an e-mail address, a corrected number. */}
      {(def?.fields || []).map((f) => (
        <label key={f.key} className="lc-field">
          <span className="lc-label">{f.label}{f.required ? ' *' : ''}</span>
          <input
            className="lc-input"
            type={f.type === 'email' ? 'email' : 'text'}
            placeholder={f.placeholder}
            value={feedback.fields?.[f.key] || ''}
            onChange={(e) => live.update({ fields: { ...(feedback.fields || {}), [f.key]: e.target.value } })}
          />
        </label>
      ))}

      {/* 3 — what next. Hidden until an outcome makes the question meaningful. */}
      {!!def && !def.terminal && (
        <>
          <div className="lc-field">
            <span className="lc-label">Next action</span>
            <div className="lc-chips">
              {NEXT_ACTIONS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => live.update({ nextAction: feedback.nextAction === a ? null : a })}
                  className={`lc-pill${feedback.nextAction === a ? ' on' : ''}`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div className="lc-field">
            <span className="lc-label">Call again{def.requiresFollowUp ? ' *' : ''}</span>
            <div className="lc-chips">
              {WHEN.map((w) => {
                const date = addDays(w.days);
                const on = sameDay(feedback.nextFollowUpAt, date);
                return (
                  <button
                    key={w.label}
                    type="button"
                    onClick={() => live.update({ nextFollowUpAt: on ? null : date.toISOString() })}
                    className={`lc-pill${on ? ' on' : ''}`}
                  >
                    {w.label}
                  </button>
                );
              })}
              <input
                className="lc-input lc-datetime"
                type="datetime-local"
                value={toLocalInput(feedback.nextFollowUpAt)}
                onChange={(e) =>
                  live.update({ nextFollowUpAt: e.target.value ? new Date(e.target.value).toISOString() : null })
                }
              />
            </div>
          </div>
        </>
      )}

      {!!def?.terminal && (
        <p className="lc-terminal">
          {def.erpStatus === 'win'
            ? 'Marks the lead Win in the ERP and closes it.'
            : def.erpStatus === 'lose'
              ? 'Marks the lead Lose in the ERP and closes it.'
              : 'Closes the record — it drops out of the queue.'}
        </p>
      )}

      {/* Reference, not task. Closed while the important thing is the notepad.
          Only in the dock — the full surface shows the history beside the panel. */}
      {variant === 'dock' && (
      <div className="lc-context">
        <button type="button" className="lc-disclosure" onClick={() => setShowContext((v) => !v)}>
          {showContext ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
          Everything said before {showContext ? '' : `· ${item.company || 'this record'}`}
        </button>
        {showContext && (
          <div className="lc-context-body">
            <NoteHistory
              notes={notes.data}
              loading={notes.isLoading}
              error={notes.isError ? notes.error : null}
              onRetry={() => notes.refetch()}
              compact
            />
          </div>
        )}
      </div>
      )}

      {canKeepContacts && (
        <div className="lc-context">
          <button type="button" className="lc-disclosure" onClick={() => setShowContact((v) => !v)}>
            {showContact ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            {!known
              ? 'Add the person you spoke to'
              : addingNew
                ? 'Add contact details'
                : `Update ${known.name || 'this contact'}’s details`}
          </button>

          {showContact && (
            <div className="lc-contactbox">
              {/* Only a call to a known contact has two possible subjects. */}
              {!!known && (
                <div className="lc-contactmodes">
                  {[['update', `Correct ${known.name || 'them'}`], ['add', 'Someone else']].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={`lc-contactmode${contactMode === key ? ' on' : ''}`}
                      onClick={() => setContactMode(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              <p className="lc-muted">
                {addingNew
                  ? 'Saved onto this account in the ERP, so the next caller has a name and a number instead of the switchboard.'
                  : 'Only what you fill in is changed — an empty box keeps what is already on file.'}
              </p>

              <div className="lc-contactgrid">
                {addingNew && (
                  <ContactField label="Name *" k="name" v={contactForm} set={setContactForm} ph="Who did you speak to?" />
                )}
                <ContactField label="Position" k="position" v={contactForm} set={setContactForm} ph="Purchase Manager" />
                <ContactField label="E-mail" k="email" v={contactForm} set={setContactForm} ph="name@company.com" />
                <ContactField label="Mobile" k="mobile" v={contactForm} set={setContactForm} ph="+968…" />
                {addingNew && (
                  <>
                    <ContactField label="Direct line" k="phone" v={contactForm} set={setContactForm} ph="+968…" />
                    <ContactField label="WhatsApp" k="whatsapp" v={contactForm} set={setContactForm} ph="+968…" />
                    <ContactField label="Location" k="location" v={contactForm} set={setContactForm} ph="Where are they based?" />
                  </>
                )}
              </div>

              {(addContact.isError || updateContact.isError) && (
                <div className="lc-error" style={{ marginTop: 10 }}>
                  {apiMessage(addContact.error || updateContact.error)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <footer className="lc-actions">
        {isLive ? (
          <button type="button" className="lc-btn danger" onClick={live.hangUp}>
            <FaPhoneSlash /> End call
          </button>
        ) : null}
        <button
          type="button"
          className="lc-btn primary"
          onClick={async () => {
            const patch = Object.fromEntries(
              Object.entries(contactForm).filter(([, v]) => String(v || '').trim())
            );
            /*
             * The contact goes first. If it fails the call is still open and can simply
             * be saved again; saving the call first would leave a closed call and a name
             * that can no longer be submitted.
             */
            if (showContact && Object.keys(patch).length) {
              try {
                if (known && !addingNew) {
                  await updateContact.mutateAsync({
                    moduleKey: item.module, recordId: item.recordId,
                    contactId: known.id, channel: known.channel, ...patch,
                  });
                } else {
                  if (!patch.name) return;
                  await addContact.mutateAsync({
                    moduleKey: item.module, recordId: item.recordId, ...patch,
                  });
                }
              } catch { return; }
            }
            live.save();
          }}
          disabled={!feedback.outcome || saving}
          title={feedback.outcome ? 'Save and move on' : 'Pick what happened first'}
        >
          <FaCheck /> {saving ? 'Saving…' : isLive ? 'Save & end' : 'Save & next'}
        </button>
      </footer>

      <p className="lc-foot">
        <FaMobileAlt size={10} /> In sync with your phone. Counts towards your figures once you submit
        the day in Review.
      </p>
    </div>
  );
}
